import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { supabase } from "../lib/supabaseClient";
import logger from "../utils/logger";

const AuthContext = createContext({});

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const initializingRef = useRef(false);
    const loggingOutRef = useRef(false);
    const profileLoadingRef = useRef(false);
    const profileRef = useRef(null);
    // Tracks when signIn() is actively running so the SIGNED_IN event handler
    // knows to stay out of the way and not race for loading state.
    const signingInRef = useRef(false);

    // ============================================================================
    // 🔒 EMAIL VERIFICATION ENFORCEMENT
    // ============================================================================
    // Checks if user has verified their email before allowing app access
    const isEmailVerified = useCallback(() => {
        if (!user) return false;
        return !!user?.email_confirmed_at;
    }, [user]);

    // Load user profile from database
    const loadProfile = useCallback(async (userId) => {
        // Prevent concurrent profile loads
        if (profileLoadingRef?.current) {
            return null;
        }

        profileLoadingRef.current = true;

        try {
            // ⚠️ CRITICAL FIX: Enhanced retry logic with exponential backoff for trigger completion
            const maxRetries = 5;
            let retryCount = 0;
            let profileData = null;

            while (retryCount < maxRetries && !profileData) {
                const { data, error } = await supabase
                    ?.from("user_profiles")
                    ?.select(
                        `
          *,
          company_profiles (*)
        `,
                    )
                    ?.eq("id", userId)
                    ?.maybeSingle();

                if (error) {
                    // Don't log AbortError as an error - it's expected when cancelling
                    if (error?.message?.includes('aborted') || error?.name === 'AbortError' || error?.message?.includes('signal is aborted')) {
                        return null;
                    }
                    logger.error("Error loading profile:", error);
                    return null;
                }

                // Check if we have user_profile
                if (data) {
                    profileData = data;
                    if (profileData?.company_profiles && !Array.isArray(profileData.company_profiles)) {
                        profileData.company_profiles = [profileData.company_profiles];
                    }
                    break;
                }

                // Profile not complete yet - wait for trigger to complete with exponential backoff
                retryCount++;
                if (retryCount < maxRetries) {
                    const delay = Math.min(1000 * Math.pow(1.5, retryCount - 1), 3000); // 1s, 1.5s, 2.25s, 3s, 3s
                    await new Promise((resolve) => setTimeout(resolve, delay));
                }
            }

            // ⚠️ CRITICAL FIX: Handle missing profile gracefully - don't block login
            if (!profileData) {
                setProfile(null);
                return null;
            }

            // Fallback: si company_name está vacío, rellenar desde raw_user_meta_data
            if (!profileData.company_profiles?.[0]?.company_name) {
                const { data: authUser } = await supabase.auth.getUser();
                if (authUser?.user?.user_metadata?.company_name) {
                    profileData.company_profiles = [{
                        ...profileData.company_profiles?.[0],
                        company_name: authUser.user.user_metadata.company_name,
                        rut: authUser.user.user_metadata.rut || profileData.company_profiles?.[0]?.rut,
                    }];
                }
            }

            // Cargar plan activo desde subscriptions (propio o del padre si es cuenta hija)
            const companyProfileId = profileData.company_profiles?.[0]?.id;
            const parentCompanyProfileId = profileData.company_profiles?.[0]?.parent_company_id;
            const subscriptionCompanyId = companyProfileId;
            if (subscriptionCompanyId) {
                try {
                    let subData = null;
                    // Buscar suscripción propia primero
                    const { data: ownSub } = await supabase
                        .from('subscriptions')
                        .select('status, plan_id, plans(slug, max_users, name)')
                        .eq('company_profile_id', subscriptionCompanyId)
                        .eq('status', 'active')
                        .maybeSingle();
                    subData = ownSub;
                    // Si no tiene suscripción propia y es hijo, buscar la del padre
                    if (!subData && parentCompanyProfileId) {
                        const { data: parentSub } = await supabase
                            .from('subscriptions')
                            .select('status, plan_id, plans(slug, max_users, name)')
                            .eq('company_profile_id', parentCompanyProfileId)
                            .eq('status', 'active')
                            .maybeSingle();
                        subData = parentSub;
                    }
                    profileData.subscription = subData || null;
                    profileData.planSlug = subData?.plans?.slug || null;
                    profileData.planMaxUsers = subData?.plans?.max_users || null;
                } catch (subErr) {
                    logger.warn('No se pudo cargar suscripción:', subErr);
                    profileData.subscription = null;
                    profileData.planSlug = null;
                    profileData.planMaxUsers = null;
                }
            }

            setProfile(profileData);
            return profileData;
        } catch (err) {
            // Don't log AbortError as an error - it's expected when requests are cancelled
            if (err?.name === 'AbortError' || err?.message?.includes('aborted') || err?.message?.includes('signal is aborted')) {
                return null;
            }
            logger.error("Profile load exception:", err);
            return null;
        } finally {
            // Clear loading flag
            profileLoadingRef.current = false;
        }
    }, []);

    // Keep profileRef in sync so onAuthStateChange closure always reads current profile
    useEffect(() => { profileRef.current = profile; }, [profile]);

    useEffect(() => {
        // CRITICAL FIX: Prevent multiple simultaneous initializations
        if (initializingRef?.current) {
            return;
        }

        const initializeAuth = async () => {
            initializingRef.current = true;

            try {
                // Check if we have a valid session in localStorage
                const {
                    data: { session },
                    error: sessionError,
                } = await supabase?.auth?.getSession();

                if (sessionError) {
                    logger.error("Session retrieval error:", sessionError);
                    setLoading(false);
                    initializingRef.current = false;
                    return;
                }

                if (session?.user) {
                    // 🔒 CRITICAL: Check email verification status
                    if (!session?.user?.email_confirmed_at) {
                        setUser(session?.user);
                        setProfile(null);
                        setLoading(false);
                        initializingRef.current = false;
                        return;
                    }

                    setUser(session?.user);
                    await loadProfile(session?.user?.id);
                } else {
                    setUser(null);
                    setProfile(null);
                }
            } catch (error) {
                logger.error("Error initializing auth:", error);
                setError(error?.message);
            } finally {
                setLoading(false);
                initializingRef.current = false;
            }
        };

        initializeAuth();

        // Listen for auth changes (login, logout, token refresh)
        const {
            data: { subscription },
        } = supabase?.auth?.onAuthStateChange(async (event, session) => {
            // CRITICAL FIX: Ignore all auth events during logout
            if (loggingOutRef?.current) {
                return;
            }

            // CRITICAL FIX: Prevent processing duplicate events during initialization
            if (initializingRef?.current && event === "SIGNED_IN") {
                return;
            }

            // Handle different auth events
            switch (event) {
                case "SIGNED_IN":
                    // signIn() is already handling credentials, profile fetch, and
                    // loading state — skip to avoid a race condition.
                    if (signingInRef.current) {
                        break;
                    }

                    // 🔒 CRITICAL: Check email verification on sign in
                    if (!session?.user?.email_confirmed_at) {
                        setUser(session?.user ?? null);
                        setProfile(null);
                        setLoading(false);
                        break;
                    }

                    setUser(session?.user ?? null);
                    if (session?.user) {
                        await loadProfile(session?.user?.id);
                    }
                    setLoading(false);
                    break;

                case "SIGNED_OUT":
                    setUser(null);
                    setProfile(null);
                    setLoading(false);
                    break;

                case "TOKEN_REFRESHED":
                    setUser(session?.user ?? null);

                    // Profile should still be valid, but reload if missing
                    if (session?.user && !profileRef.current) {
                        await loadProfile(session?.user?.id);
                    }
                    setLoading(false);
                    break;

                case "USER_UPDATED":
                    setUser(session?.user ?? null);

                    // 🔒 CRITICAL: Reload profile when email is verified
                    if (session?.user?.email_confirmed_at) {
                        await loadProfile(session?.user?.id);
                    }
                    setLoading(false);
                    break;

                default:
                    // Handle any other events
                    setUser(session?.user ?? null);

                    if (session?.user && !profileRef.current) {
                        await loadProfile(session?.user?.id);
                    }
                    setLoading(false);
            }
        });

        // Cleanup
        return () => {
            subscription?.unsubscribe();
            initializingRef.current = false;
            profileLoadingRef.current = false;
        };
    }, [loadProfile]);

    const signIn = async (email, password) => {
        try {
            setError(null);
            setLoading(true);
            signingInRef.current = true;

            const { data, error } = await supabase?.auth?.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;

            // Set user immediately so the UI can react, then await the profile
            // so that loading stays true until everything is ready for navigation.
            if (data?.user) {
                setUser(data.user);
                await loadProfile(data.user.id);
            }

            return data;
        } catch (error) {
            setError(error?.message);
            throw error;
        } finally {
            // loading goes false only after credentials + profile are both resolved.
            setLoading(false);
            signingInRef.current = false;
        }
    };

    const signUp = async (email, password, userData) => {
        try {
            setError(null);
            setLoading(true);

            // Validate role - ensure it's a valid enum value (buyer, seller, both)
            // Database enum: 'admin', 'buyer', 'seller', 'both'
            const validRoles = ["buyer", "seller", "both"];
            const userRole = userData?.role || "buyer";

            // Admin users should NOT be created through public signup
            if (userRole === "admin") {
                throw new Error("Admin accounts must be created by existing administrators.");
            }

            // Validate the role is a valid enum value
            if (!validRoles.includes(userRole)) {
                throw new Error("Tipo de cuenta inválido. Seleccione comprador, proveedor o ambos.");
            }

            // 🔒 CRITICAL FIX: Configure email verification WITHOUT auto-login
            // First, try signup with minimal metadata to avoid trigger issues
            let data, error;

            try {
                const result = await supabase?.auth?.signUp({
                    email,
                    password,
                    options: {
                        data: {
                            full_name: userData?.fullName || "",
                            position: userData?.position || "",
                            mobile: userData?.mobile || "",
                            office_phone: userData?.officePhone || "",
                            linkedin: userData?.linkedin || "",
                            role: userRole, // This will be 'buyer', 'seller', or 'both'
                            company_name: userData?.companyName || "",
                            rut: userData?.rut || "",
                            industry: userData?.industry || "",
                            company_size: userData?.companySize || "",
                            website: userData?.website || "",
                            region: userData?.region || "",
                            city: userData?.city || "",
                            address: userData?.address || "",
                            description: userData?.description || "",
                            user_type: userRole,
                        },
                        // 🔒 This is where user will be redirected after email confirmation
                        emailRedirectTo: `${window?.location?.origin}/email-verification`,
                        // 🔒 CRITICAL: Don't auto-login on signup - require email verification first
                        shouldCreateSession: false,
                    },
                });
                data = result?.data;
                error = result?.error;
            } catch (signupErr) {
                logger.error("Signup exception:", signupErr);
                error = signupErr;
            }

            // If we get a database error, it might be a trigger issue
            // Try again with simpler metadata
            if (error?.message?.includes("Database error")) {
                const retryResult = await supabase?.auth?.signUp({
                    email,
                    password,
                    options: {
                        data: {
                            full_name: userData?.fullName || email?.split("@")?.[0] || "",
                            role: userRole || userData?.role || "buyer",
                        },
                        emailRedirectTo: `${window?.location?.origin}/email-verification`,
                        shouldCreateSession: false,
                    },
                });

                data = retryResult?.data;
                error = retryResult?.error;

                // Store the role separately for later profile creation
                if (!error && data?.user) {
                    // Store pending profile data
                    sessionStorage?.setItem("pending_user_role", userRole);
                    sessionStorage?.setItem("pending_user_data", JSON?.stringify(userData));
                }
            }

            if (error) {
                logger.error("Signup error:", error);
                throw error;
            }

            if (!data?.user) {
                throw new Error("Error al crear la cuenta. Por favor, intenta nuevamente.");
            }

            // Crear company_profile con reintentos automáticos
            if (data?.user?.id) {
                (async () => {
                    try {
                        // Esperar a que el trigger handle_new_user cree user_profiles
                        await new Promise(resolve => setTimeout(resolve, 1500));

                        const websiteValue = userData?.website?.trim()
                            ? (userData.website.trim().match(/^https?:\/\//i)
                                ? userData.website.trim()
                                : 'https://' + userData.website.trim())
                            : null;

                        const rpcParams = {
                            p_user_id:     data.user.id,
                            p_company_name: userData?.companyName?.trim() || '',
                            p_rut:         userData?.rut?.trim() || '',
                            p_industry:    userData?.industry || null,
                            p_company_size: userData?.companySize || null,
                            p_phone:       userData?.phone?.trim() || null,
                            p_website:     websiteValue,
                            p_region:      userData?.region || null,
                            p_city:        userData?.city?.trim() || null,
                            p_address:     userData?.address?.trim() || null,
                            p_description: userData?.description?.trim() || null,
                            p_user_type:   userRole,
                        };

                        let rpcSuccess = false;
                        for (let attempt = 1; attempt <= 3; attempt++) {
                            const { error: rpcError } = await supabase.rpc('register_company_profile', rpcParams);
                            if (!rpcError) {
                                logger.log(`✅ company_profile creado (intento ${attempt})`);
                                rpcSuccess = true;
                                break;
                            }
                            logger.warn(`⚠️ RPC intento ${attempt} falló:`, rpcError.message);
                            if (attempt < 3) {
                                await new Promise(resolve => setTimeout(resolve, 1000));
                            }
                        }

                        // Fallback: INSERT directo si el RPC falló 3 veces
                        if (!rpcSuccess) {
                            logger.warn("⚠️ RPC agotado, intentando INSERT directo...");
                            const { error: insertError } = await supabase
                                .from('company_profiles')
                                .insert({
                                    user_id:      data.user.id,
                                    company_name: userData?.companyName?.trim() || '',
                                    rut:          userData?.rut?.trim() || '',
                                    industry:     userData?.industry || null,
                                    company_size: userData?.companySize || null,
                                    phone:        userData?.phone?.trim() || '',
                                    website:      websiteValue,
                                    region:       userData?.region || '',
                                    city:         userData?.city?.trim() || '',
                                    address:      userData?.address?.trim() || '',
                                    description:  userData?.description?.trim() || null,
                                    user_type:    userRole,
                                });
                            if (insertError) {
                                logger.error("❌ INSERT directo también falló:", insertError.message);
                            } else {
                                logger.log("✅ company_profile creado via INSERT directo");
                            }
                        }
                    } catch (profileErr) {
                        logger.warn("Error al crear company_profile (no bloqueante):", profileErr);
                    }
                })();
            }

            return data;
        } catch (error) {
            setError(error?.message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const signOut = async () => {
        try {
            setError(null);
            setLoading(true);

            // CRITICAL: Clear local state immediately BEFORE calling Supabase signOut
            setUser(null);
            setProfile(null);

            // Call Supabase signOut to invalidate session on server
            const { error } = await supabase?.auth?.signOut();
            if (error) {
                logger.error("Supabase signOut error:", error);
                // Don't throw - we already cleared local state
            }

            // Clear any cached data
            try {
                localStorage.removeItem(
                    "sb-" + supabase?.supabaseUrl?.split("//")?.[1]?.split(".")?.[0] + "-auth-token",
                );
            } catch (e) {
                // Ignore storage errors
            }

            setLoading(false);

            // Return success - UserMenu will handle navigation
            return { success: true };
        } catch (error) {
            logger.error("SignOut exception:", error);
            setError(error?.message);
            setLoading(false);
            // Even on error, return success since local state is cleared
            return { success: true };
        }
    };

    const updateProfile = async (updates) => {
        try {
            setError(null);
            if (!user) throw new Error("No user logged in");

            const { data, error } = await supabase
                ?.from("user_profiles")
                ?.update(updates)
                ?.eq("id", user?.id)
                ?.select()
                ?.single();

            if (error) throw error;

            setProfile(data);
            return data;
        } catch (error) {
            setError(error?.message);
            throw error;
        }
    };

    // Force reload of profile from Supabase (e.g. after updating user_profiles)
    const refreshProfile = useCallback(async () => {
        if (!user?.id) return null;
        profileLoadingRef.current = false; // Reset guard to allow forced reload
        return await loadProfile(user.id);
    }, [user?.id, loadProfile]);

    // Helper to check if user needs company profile
    const needsCompanyProfile = () => {
        if (!profile?.role) return false;
        // All non-admin users (buyer, seller, both) may need company profiles
        return profile?.role !== "admin";
    };

    // Helper to check if user is a buyer
    // Database uses 'role' field in user_profiles with values: 'admin', 'buyer', 'seller', 'both'
    const isBuyer = () => {
        if (!profile?.role) return false;
        return profile?.role === "buyer" || profile?.role === "both" || profile?.role === "buyer_child";
    };

    // Helper to check if user is a seller
    const isSeller = () => {
        if (!profile?.role) return false;
        return profile?.role === "seller" || profile?.role === "both" || profile?.role === "seller_child";
    };

    const value = useMemo(() => ({
        user,
        profile,
        loading,
        error,
        signIn,
        signUp,
        signOut,
        updateProfile,
        refreshProfile,
        // 🔒 Add email verification helpers
        isEmailVerified: isEmailVerified(),
        needsEmailVerification: user && !user?.email_confirmed_at,
        // Simplified role helpers - using actual enum values: 'admin', 'buyer', 'seller', 'both'
        userRole: profile?.role || "buyer",
        isAdmin: profile?.role === "admin",
        // User type helpers (buyer/seller/both)
        isBuyer: isBuyer(),
        isSeller: isSeller(),
        needsCompanyProfile: needsCompanyProfile(),
        // 🔒 Account status helpers
        accountStatus: profile?.status || null,
        isPendingApproval: profile?.status === "pending",
        isAccountSuspended: profile?.status === "suspended",
        isAccountRejected: profile?.status === "rejected",
        isAccountActive: profile?.status === "active",
    }), [user, profile, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
